<template>
    <div>
        <div
            v-if="legends.length"
            class="d-flex flex-wrap justify-content-center mx-n2 mb-4"
        >
            <p
                v-for="(legend, index) in legends"
                :key="index"
                class="d-flex p-2 m-0 text-small"
            >
                <span
                    :style="{ backgroundColor: getLegendColor(index) }"
                    class="legend mr-1"
                />
                <span class="d-flex flex-column">
                    <strong>{{ legend.title }}</strong>
                    {{ legend.subtitle }}
                </span>
            </p>
        </div>

        <bar-chart
            ref="chart"
            :height="250"
            :chart-data="chartData"
            :options="options"
            class="w-100 chart"
        ></bar-chart>
    </div>
</template>

<script>
    import { BarChart } from '../charts';

    export default {
        name: 'WidgetBarChart',
        components: {
            BarChart,
        },
        props: {
            data: {
                type: Object,
                default: () => {},
            },
            isInteractive: {
                type: Boolean,
                default: false,
            },
            legends: {
                type: Array,
                default: () => [],
            },
            max: {
                type: Number,
                default: 7,
            },
            showYAxis: {
                type: Boolean,
                default: true,
            },
            stepSize: {
                type: Number,
                default: 1,
            },
            type: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                barColor: '#dfdddd',
                barColorActive: '#444',
                chartData: [],
                options: {
                    layout: {
                        padding: {
                            top: 1,
                        },
                    },
                    maintainAspectRatio: false,
                    responsive: true,
                    scales: {
                        yAxes: [{
                            display: this.showYAxis,
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#302e2e',
                                fontStyle: '500',
                                max: this.max,
                                padding: 20,
                                stepSize: this.stepSize,
                            },
                            gridLines: {
                                display: false,
                                drawBorder: false,
                                drawTicks: false,
                                offsetGridLines: true,
                            },
                        }],
                        xAxes: [{
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#302e2e',
                                padding: 10,
                            },
                            gridLines: {
                                display: false,
                                drawBorder: false,
                                drawTicks: false,
                                zeroLineColor: 'transparent',
                            },
                        }],
                        scaleLabel: {
                            display: true,
                        },
                    },
                    gridLines: {
                        color: 'rgba(242,247,246,1)',
                    },
                    legend: {
                        labels: {
                            fontColor: '#556F7B',
                        },
                        display: false,
                    },
                    onClick: this.handleClick,
                    tooltips: {
                        callbacks: {
                            label: function(t, d) {
                                const dataset = d.datasets[t.datasetIndex];

                                const label = dataset.titles[t.index];

                                if (typeof label === 'object') {
                                    const link = dataset.links[t.index];

                                    if (label.override && link) {
                                        return 'Klik voor details';
                                    }

                                    return label.override ? label.text : dataset.data[t.index] + label.text;
                                }

                                return dataset.data[t.index] + label;
                            }
                        },
                    },
                },
            };
        },
        mounted() {
            this.mapData();
        },
        methods: {
            async mapData() {
                this.chartData = this.data;
                this.chartData.datasets = this.chartData.datasets.map(dataset => ({
                    backgroundColor: Array(this.chartData.datasets.length).fill('#dfdddd'),
                    borderColor: Array(this.chartData.datasets.length).fill(''),
                    borderWidth: 2,
                    minBarLength: 1,
                    ...dataset,
                    data: dataset.data.map((x) => x.data),
                    titles: dataset.data.map((x) => x.label),
                    links: dataset.data.map((x) => x.link),
                }));

                const hasId = this.chartData.datasets
                    .some(item => item.data
                        .some(data => data && data.hasOwnProperty('value')));

                if (hasId) {
                    this.chartData.datasets.forEach(dataset => {
                        dataset.data = dataset.data.map(item => item.value);
                    });

                    const activeBarIndex = this.chartData.datasets[0].data
                        .filter(item => item > 0).length - 1;

                    this.chartData.datasets[0].borderColor[activeBarIndex] = this.barColorActive;
                }

                this.chartData.datasets[0].data.forEach((item, index) => {
                    if (this.type === 'BMI') {
                        this.chartData.datasets[0].backgroundColor[index] =
                            this.getBMIColor(item);
                    } else {
                        const scoreNormalized = Math.max((item / (this.max / 7)), 1);
                        const reverse = this.type === 'LAST';

                        this.chartData.datasets[0].backgroundColor[index] =
                            this.getColorByScore(scoreNormalized, reverse);
                    }
                });
            },

            handleClick: function(event, points) {
                const link = this.chartData.datasets[0].links[points[0]._index] || null;

                if (link !== null) {
                    window.open(link, '_blank');
                }

                if (!this.isInteractive || !points[0]) {
                    return false;
                }

                const chart = this.$refs.chart._data._chart;
                const index = points[0]._index;
                const chartBorderColor = this.chartData.datasets[0].borderColor;

                chartBorderColor.forEach((item, index) => chartBorderColor[index] = '');
                chartBorderColor[index] = this.barColorActive;

                chart.update();
                this.$emit('barClick', index);
            },

            getBMIColor(value) {
                if (value >= 19 && value <= 25) {
                    return '#54A254';
                }

                if (value >= 25 && value <= 30) {
                    return '#F5CB46';
                }

                return '#B14F57';
            },

            getLegendColor(index) {
                if (this.type === 'BMI') {
                    const colors = ['#B14F57', '#F5CB46', '#54A254', '#B14F57'];

                    return colors[index];
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .legend {
        width: 12px;
        height: 12px;
        margin-top: 2px;
        background-color: $info;
    }
</style>
